import React from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../../i18n/TModules';
import { PlainButton } from '../../../components/pageStyles';
import { limitErrors } from '../../adapters/limits';
import { levelPrizeErrors } from '../../adapters/progress';
import StepPanel from '../../kit/StepPanel';
import { Form } from '../../kit/layout';
import { EnrolmentFields, ScheduleFields, WindowLine } from '../../kit/limitFields';
import { IWizardStepProps, newPrize, WizardLimits } from '../../wizardModel';
import { tint, wz, wzType } from '../../wizardStyles';
import PrizeList from '../PrizeList';

const TW = `${TOrbit}.wizard`;
const TL = `${TW}.limits`;

// 6778:24207's level tabs: the active one in the accent, on a tint.
const Tabs = styled.div`
  display: flex;
  margin-bottom: 16px;

  > * + * {
    margin-left: 8px;
  }
`;

const Tab = styled(PlainButton)<{ $active: boolean }>`
  padding: 8px 16px;
  border: ${wz.line} solid ${({ $active }) => ($active ? wz.accent : wz.border)};
  border-radius: 999px;
  background: ${({ $active }) => ($active ? tint(wz.accent, 0.14) : wz.field)};
  color: ${({ $active }) => ($active ? wz.accent : wz.secondary)};
  font-weight: 600;
  ${wzType.control}
`;

/** Step 4/5 of Progress: the prizes of each level, a tab per level. */
export const LevelPrizeStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const active = Math.min(state.activeLevel, state.levels.length - 1);
  const level = state.levels[active];
  const n = active + 1;

  return (
    <StepPanel
      step="prizes"
      narrow
      title={t(`${TW}.setup.progress`)}
      question={t(`${TW}.prize.levelQuestion`, { n })}
      errors={levelPrizeErrors(state.levels, t)}
      onNext={next}
      onBack={back}
    >
      <Tabs role="tablist">
        {state.levels.map((_, index) => {
          const end = index === state.levels.length - 1;
          return (
            <Tab
              // Levels are their position: Level n is always the n-th.
              key={index}
              type="button"
              role="tab"
              aria-selected={index === active}
              $active={index === active}
              onClick={() => update(current => ({ ...current, activeLevel: index }))}
            >
              {t(`${TW}.milestones.${end ? 'levelEnd' : 'level'}`, { n: index + 1 })}
            </Tab>
          );
        })}
      </Tabs>
      {level !== undefined ? (
        <PrizeList
          addLabel={t(`${TW}.prize.addPrize`)}
          key={active}
          name={`orbit-wizard-level-${active}`}
          prizes={level.prizes}
          eyebrow={() => t(`${TW}.prize.levelPrize`, { n })}
          blank={newPrize}
          onChange={prizes =>
            update(current => ({
              ...current,
              levels: current.levels.map((item, at) => (at === active ? { ...item, prizes } : item))
            }))
          }
        />
      ) : null}
    </StepPanel>
  );
};

/** 6778:24452, Step 5/5: enrolment and its window, then the dates. */
export const ProgressLimitsStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const { limits } = state;
  const change = (patch: Partial<WizardLimits>) =>
    update(current => ({ ...current, limits: { ...current.limits, ...patch } }));

  return (
    <StepPanel
      step="limits"
      narrow
      title={t(`${TW}.setup.progress`)}
      question={t(`${TL}.question`)}
      errors={limitErrors(state, t)}
      onNext={next}
      onBack={back}
    >
      <Form>
        <EnrolmentFields limits={limits} onChange={change} withTime>
          {!limits.autoEnrol ? (
            <WindowLine limits={limits} onChange={change} leadIn={t(`${TL}.window.progress`)} />
          ) : null}
        </EnrolmentFields>
        <ScheduleFields limits={limits} onChange={change} />
      </Form>
    </StepPanel>
  );
};
